import React from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { ModalProps } from '@/hooks/use-modal';

const MAX_VISIBLE_NAMES = 5;

interface DeleteModalI18nKeys {
  title: string;
  text: string;
  cancel: string;
  confirm: string;
  andMore: string;
}

interface ConfirmDeleteEntityModalProps extends ModalProps {
  selectedNames: string[];
  onConfirm: () => void;
  isLoading?: boolean;
  /** Fully-qualified i18n keys for the delete modal (e.g. 'authenticated.onboarding.projects:deleteModal.title') */
  i18nKeys: DeleteModalI18nKeys;
}

export const ConfirmDeleteEntityModal: React.FC<ConfirmDeleteEntityModalProps> = ({
  selectedNames,
  onClose,
  onConfirm,
  isLoading,
  i18nKeys,
  ...props
}) => {
  const { t } = useTranslation();

  const visibleNames = selectedNames.slice(0, MAX_VISIBLE_NAMES);
  const remainingNames = selectedNames.slice(MAX_VISIBLE_NAMES);
  const remainingCount = remainingNames.length;

  return (
    <Modal onClose={onClose} className="max-w-lg" {...props}>
      <Modal.Title className="mb-2">
        {t(i18nKeys.title, { count: selectedNames.length })}
      </Modal.Title>
      <Modal.Body className="mb-4 py-0 text-sm text-muted-foreground">
        <div>
          <Trans
            i18nKey={i18nKeys.text}
            values={{ names: visibleNames.join(', ') }}
            count={selectedNames.length}
            components={{
              b: <b />,
              andMore:
                remainingCount > 0 ? (
                  <SimpleTooltip content={remainingNames.join(', ')} contentClassName="max-w-md">
                    <span className="cursor-help font-bold">
                      {t(i18nKeys.andMore, { count: remainingCount })}
                    </span>
                  </SimpleTooltip>
                ) : (
                  <></>
                ),
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t(i18nKeys.cancel)}
          </Button>
          <ButtonLoadable variant="error" onClick={onConfirm} isLoading={isLoading}>
            {t(i18nKeys.confirm, { count: selectedNames.length })}
          </ButtonLoadable>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
